import { compareAsc, parseISO } from "date-fns";
import moment from "moment";
import { openSnackbar } from "../context/SnackbarContext";
import {
  Asset,
  ComplianceFramework,
  LLMAgentOutput,
  MetricResult,
  TestCase,
  TestResult,
  TestResultStatus,
  WorkflowRun,
  WorkflowRunOutput,
} from "../models/types";
import { Theme } from "@mui/material";

type IResultType = {
  lineChartData: {
    labels: string[];
    values: {
      name: string;
      data: number[];
    }[];
  }[];
  pieData: {
    values: number[];
    labels: string[];
    type: string;
  }[];
  colors?: string[];
};

type IMetricResultType = {
  lineChartData: {
    labels: string[];
    values: {
      name: string;
      data: number[];
    }[];
  }[];
};

type StatusData = {
  [status: string]: number;
};

type MetricResultData = {
  [key: string]: number;
};

const statusColor = (label: string, theme: Theme): string => {
  switch (label) {
    case "Passed":
      return theme.palette.custom.greenTypography;
    case "Failed":
      return theme.palette.custom.redTypography;
    case "Untested":
    default:
      return theme.palette.custom.grayTypography;
  }
};

const getLastResults = (results: TestResult[]): TestResult[] => {
  const mapping: { [key: string]: TestResult } = {};

  results.forEach((result) => {
    const testCaseKey =
      typeof result.test_case === "string" ? result.test_case : result.test_case.id;
    if (
      !mapping[testCaseKey] ||
      compareAsc(parseISO(mapping[testCaseKey].created_on), parseISO(result.created_on)) < 0
    ) {
      mapping[testCaseKey] = result;
    }
  });
  return Object.values(mapping);
};

const groupByEvaluationRunAndTestCase = (results: TestResult[]): TestResult[][] => {
  const groupedResults = results.reduce((grouped, currentResult) => {
    const testCaseId =
      typeof currentResult.test_case === "string"
        ? currentResult.test_case
        : currentResult.test_case.id;

    const groupKey = `${testCaseId}_${currentResult.evaluation_run}`;

    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }

    grouped[groupKey].push(currentResult);

    return grouped;
  }, {} as Record<string, TestResult[]>);

  return Object.values(groupedResults);
};

export const transformResultsToChartData = (
  results: TestResult[] | undefined,
  timeFilter: string,
  statuses: TestResultStatus[],
  onlyLastResult: boolean = false,
  testCases: TestCase[] = [],
  theme: Theme
): IResultType => {
  if (!results || results.length === 0) {
    return {
      lineChartData: Array(1).fill({
        labels: [],
        values: [],
      }),
      pieData: [
        {
          values: [],
          labels: [],
          type: "pie",
        },
      ],
    };
  }
  const resultsArray = onlyLastResult ? getLastResults(results) : results;
  const timeFilterFormats: Record<string, string> = {
    Days: "D/MM",
    Week: "w/YYYY",
    Month: "MMMM YYYY",
  };
  const groupByFormat = timeFilterFormats[timeFilter];

  const groupedData: { [key: string]: StatusData } = {};

  resultsArray.forEach((result) => {
    const date = moment(result.created_on);
    const key = date.format(groupByFormat);

    if (!groupedData[key]) {
      groupedData[key] = {
        Passed: 0,
        Failed: 0,
        Untested: onlyLastResult ? testCases.length : 0,
      };
    }

    groupedData[key][result.status]++;
    if (onlyLastResult) {
      groupedData[key]["Untested"]--;
    }
  });

  const sortedDates = Object.keys(groupedData)
    .map((date) => moment(date, groupByFormat).toDate())
    .sort((a, b) => a.getTime() - b.getTime())
    .map((date) => moment(date).format(groupByFormat));

  const nonZeroStatuses = statuses.filter((status) => {
    return sortedDates.some((date) => (groupedData[date][status] || 0) > 0);
  });

  const lineChartData = [
    {
      labels: sortedDates,
      values: nonZeroStatuses.map((status) => ({
        name: status,
        data: sortedDates.map((date) => groupedData[date][status] || 0),
      })),
    },
  ];

  const pieChartData = [
    {
      values: nonZeroStatuses.map((status) => {
        const emptyTestCases = testCases.length === 0;
        if (status === "Untested" && !emptyTestCases) {
          return (
            testCases.length - resultsArray.filter((result) => result.status !== "Untested").length
          );
        }
        return resultsArray.filter((result) => result.status === status).length;
      }),
      labels: nonZeroStatuses,
      type: "pie",
    },
  ];

  const colors: string[] = nonZeroStatuses.map((status) => statusColor(status, theme));

  return { lineChartData, pieData: pieChartData, colors };
};

export const transformMetricResultsToChartData = (
  results: MetricResult[] | undefined,
  timeFilter: string,
  reducers: string[]
): IMetricResultType => {
  if (!results || results.length === 0) {
    return {
      lineChartData: Array(3).fill({
        labels: [],
        values: [],
      }),
    };
  }
  const resultsArray = results;
  const timeFilterFormats: Record<string, string> = {
    Days: "D/MM",
    Week: "w/YYYY",
    Month: "MMMM YYYY",
  };
  const groupByFormat = timeFilterFormats[timeFilter];

  const groupedData: { [key: string]: MetricResultData } = {};

  resultsArray.forEach((result) => {
    const date = moment(result.created_on);
    const key = date.format(groupByFormat);

    if (!groupedData[key]) {
      groupedData[key] = {
        Count: 0,
        Average: 0,
        Sum: 0,
      };
    }

    groupedData[key]["Count"]++;
    try {
      const value = Number(result.value);
      if (!isNaN(value)) {
        groupedData[key]["Sum"] += value;
        groupedData[key]["Average"] = groupedData[key]["Sum"] / groupedData[key]["Count"];
      }
    } catch (error) {
      openSnackbar("Error parsing metric result value", "error");
    }
  });

  const sortedDates = Object.keys(groupedData)
    .map((date) => moment(date, groupByFormat).toDate())
    .sort((a, b) => a.getTime() - b.getTime())
    .map((date) => moment(date).format(groupByFormat));

  const lineChartData = [
    {
      labels: sortedDates,
      values: reducers.map((reducer) => ({
        name: reducer,
        data: sortedDates.map((date) => groupedData[date][reducer] || 0),
      })),
    },
  ];

  return { lineChartData };
};

export const transformFrameworkControlsToChartData = (
  frameworks: ComplianceFramework[] | undefined
): IResultType => {
  if (!frameworks || frameworks.length === 0) {
    return {
      lineChartData: Array(3).fill({
        x: [],
        y: [],
        name: "",
        mode: "lines",
      }),
      pieData: [
        {
          values: [],
          labels: [],
          type: "pie",
        },
      ],
    };
  }

  const frameworksArray = frameworks;

  const groupedData: { [key: string]: number } = {};

  groupedData["Not Started"] = 0;
  groupedData["In Progress"] = 0;
  groupedData["Completed"] = 0;

  frameworksArray.forEach((framework) => {
    let key = "";

    if (framework.progress.completed === framework.progress.total) {
      key = "Completed";
    } else if (framework.progress.completed === 0) {
      key = "Not Started";
    } else {
      key = "In Progress";
    }

    groupedData[key]++;
  });

  const pieChartData = [
    {
      values: Object.values(groupedData),
      labels: Object.keys(groupedData),
      type: "pie",
    },
  ];

  return { lineChartData: [], pieData: pieChartData };
};

export const transformAssetsToChartData = (assets: Asset[] | undefined): IResultType => {
  if (!assets || assets.length === 0) {
    return {
      lineChartData: Array(3).fill({
        labels: [],
        values: [],
      }),
      pieData: [
        {
          values: [],
          labels: [],
          type: "pie",
        },
      ],
    };
  }

  const assetsArray = assets;

  const groupedData: { [key: string]: number } = {};

  groupedData["DRAFT"] = 0;
  groupedData["NEEDS APPROVAL"] = 0;
  groupedData["ACTION REQUIRED"] = 0;
  groupedData["NO POLICY"] = 0;
  groupedData["NO DOCUMENT"] = 0;
  groupedData["APPROVED"] = 0;
  groupedData["EXCLUDED"] = 0;

  assetsArray.forEach((asset) => {
    let key = "";

    if (asset.status === "APPROVED") {
      key = "APPROVED";
    } else if (asset.status === "DRAFT") {
      key = "DRAFT";
    } else if (asset.status === "NEEDS APPROVAL") {
      key = "NEEDS APPROVAL";
    } else if (asset.status === "ACTION REQUIRED") {
      key = "ACTION REQUIRED";
    } else if (asset.status === "NO POLICY") {
      key = "NO POLICY";
    } else if (asset.status === "NO DOCUMENT") {
      key = "NO DOCUMENT";
    } else if (asset.status === "EXCLUDED") {
      key = "EXCLUDED";
    }

    groupedData[key]++;
  });

  const pieChartData = [
    {
      values: Object.values(groupedData),
      labels: Object.keys(groupedData),
      type: "pie",
    },
  ];

  return { lineChartData: [], pieData: pieChartData };
};

export const transformResultsToEvaluationRunListChartData = (
  results: TestResult[] | undefined,
  timeFilter: string,
  statuses: TestResultStatus[],
  theme: Theme
): IResultType => {
  if (!results || results.length === 0) {
    return {
      lineChartData: Array(1).fill({
        labels: [],
        values: [],
      }),
      pieData: [
        {
          values: [],
          labels: [],
          type: "pie",
        },
      ],
    };
  }
  const timeFilterFormats: Record<string, string> = {
    Days: "D/MM",
    Week: "w/YYYY",
    Month: "MMMM YYYY",
  };
  const groupByFormat = timeFilterFormats[timeFilter];

  const groupedData: { [key: string]: StatusData } = {};
  const resultsArray: TestResult[] = [];

  const groupedResults = groupByEvaluationRunAndTestCase(results);
  groupedResults.map((result) => {
    const lastResults = getLastResults(result);
    lastResults.forEach((lastResult) => {
      resultsArray.push(lastResult);
    });
  });

  resultsArray.forEach((result) => {
    const date = moment(result.created_on);
    const key = date.format(groupByFormat);

    if (!groupedData[key]) {
      groupedData[key] = {
        Passed: 0,
        Failed: 0,
      };
    }

    groupedData[key][result.status]++;
  });

  // Remove keys where all values are zero
  Object.keys(groupedData).forEach((key) => {
    const values = groupedData[key];
    const allZero = Object.values(values).every((val) => val === 0);

    if (allZero) {
      delete groupedData[key];
    }
  });

  const sortedDates = Object.keys(groupedData)
    .map((date) => moment(date, groupByFormat).toDate())
    .sort((a, b) => a.getTime() - b.getTime())
    .map((date) => moment(date).format(groupByFormat));

  const nonZeroStatuses = statuses.filter((status) => {
    return sortedDates.some((date) => (groupedData[date][status] || 0) > 0);
  });

  const lineChartData = [
    {
      labels: sortedDates,
      values: nonZeroStatuses.map((status) => ({
        name: status,
        data: sortedDates.map((date) => groupedData[date][status] || 0),
      })),
    },
  ];

  const pieChartData = [
    {
      values: nonZeroStatuses.map((status) => {
        return resultsArray.filter((result) => result.status === status).length;
      }),
      labels: nonZeroStatuses,
      type: "pie",
    },
  ];

  const colors: string[] = nonZeroStatuses.map((status) => statusColor(status, theme));

  return { lineChartData, pieData: pieChartData, colors };
};

export const transformWorkflowRunsToTrendsChartData = (
  workflowRuns: WorkflowRun[],
  timeFrame: string,
  selectedMetric: "success_rate" | "failed" | "average_duration" | "cost" | string,
  _startTime: string,
  endTime: string
): {
  labels: number[];
  values: {
    name: string;
    data: number[];
  }[];
} => {
  if (!workflowRuns || workflowRuns.length === 0) {
    return {
      labels: [],
      values: [],
    };
  }

  // Sort runs by creation time
  const sortedRuns = [...workflowRuns].sort(
    (a, b) => moment(a.created_on).valueOf() - moment(b.created_on).valueOf()
  );

  if (sortedRuns.length === 0) {
    return { labels: [], values: [] };
  }

  // Determine time intervals based on timeFrame
  let intervalMinutes = 10; // Default for "1h"

  switch (timeFrame) {
    case "1h":
      intervalMinutes = 10; // 10 minute intervals
      break;
    case "6h":
      intervalMinutes = 60; // 1 hour intervals
      break;
    case "12h":
      intervalMinutes = 120; // 2 hour intervals
      break;
    case "24h":
      intervalMinutes = 240; // 4 hour intervals
      break;
    default:
      intervalMinutes = 10; // Default to 10 minute intervals
  }

  const startTime = moment(_startTime);
  const latestRun = moment(endTime);

  // Generate time labels based on interval
  const timeLabels: number[] = [];
  let currentTime = moment(startTime);

  while (currentTime <= latestRun) {
    timeLabels.push(currentTime.valueOf());
    currentTime = moment(currentTime).add(intervalMinutes, "minutes");
  }

  // Group workflow runs by workflow name
  const workflowRunsByName: Record<string, WorkflowRun[]> = {};

  workflowRuns.forEach((run) => {
    const workflowName = run.workflow_name?.name || "Unknown Workflow";

    if (!workflowRunsByName[workflowName]) {
      workflowRunsByName[workflowName] = [];
    }

    workflowRunsByName[workflowName].push(run);
  });

  // Calculate metrics for each workflow by time interval
  const values = Object.entries(workflowRunsByName).map(([workflowName, runs]) => {
    // Group runs by time interval
    const runsByInterval: Record<
      string,
      {
        total: number;
        completed: number;
        failed: number;
        averageDuration: number;
        cost: number;
        totalDuration?: number;
        runsWithDuration?: number;
      }
    > = {};

    // Initialize all intervals with zero values
    timeLabels.forEach((label) => {
      runsByInterval[label] = {
        total: 0,
        completed: 0,
        failed: 0,
        averageDuration: 0,
        cost: 0,
        totalDuration: 0,
        runsWithDuration: 0,
      };
    });

    // Populate with actual run data
    runs.forEach((run) => {
      const runTime = moment(run.created_on);

      // Skip runs outside our time window
      if (runTime < startTime || runTime > latestRun) {
        return;
      }

      // Find the appropriate time label for this run
      const labelTime = moment(startTime);
      let matchingLabel: number = 0;

      while (labelTime <= latestRun) {
        const nextLabelTime = moment(labelTime).add(intervalMinutes, "minutes");

        if (runTime >= labelTime && runTime < nextLabelTime) {
          matchingLabel = labelTime.valueOf();
          break;
        }

        labelTime.add(intervalMinutes, "minutes");
      }

      if (matchingLabel && runsByInterval[matchingLabel]) {
        runsByInterval[matchingLabel].total += 1;

        if (run.status === "COMPLETED") {
          runsByInterval[matchingLabel].completed += 1;

          let durationSeconds = 0;

          if (typeof run.duration === "number" && !isNaN(run.duration) && run.duration > 0) {
            durationSeconds = run.duration;
          } else if (run.updated_on && run.created_on) {
            const created = new Date(run.created_on).getTime();
            const updated = new Date(run.updated_on).getTime();
            if (!isNaN(created) && !isNaN(updated) && updated > created) {
              durationSeconds = (updated - created) / 1000;
            }
          }

          // Accumulate for average calculation
          if (durationSeconds > 0) {
            runsByInterval[matchingLabel].totalDuration =
              (runsByInterval[matchingLabel].totalDuration ?? 0) + durationSeconds;
            runsByInterval[matchingLabel].runsWithDuration =
              (runsByInterval[matchingLabel].runsWithDuration ?? 0) + 1;

            runsByInterval[matchingLabel].averageDuration = Math.round(
              (runsByInterval[matchingLabel].totalDuration ?? 0) /
                (runsByInterval[matchingLabel].runsWithDuration ?? 1)
            );
          }
        } else if (run.status === "FAILED") {
          runsByInterval[matchingLabel].failed += 1;
        }
        // Add run cost to the interval's total cost
        const runCost = run.cost !== undefined && run.cost !== null ? Number(run.cost) : 0;
        if (!isNaN(runCost)) {
          runsByInterval[matchingLabel].cost += runCost;
        }
      }
    });

    // Calculate the selected metric for each interval
    const metricValues = timeLabels.map((label) => {
      const intervalData = runsByInterval[label];

      if (!intervalData) return 0;

      switch (selectedMetric) {
        case "success_rate":
          return intervalData.total > 0
            ? Math.round((intervalData.completed / intervalData.total) * 100)
            : 0;
        case "failures":
          return intervalData.failed;
        case "average_duration":
          return intervalData.averageDuration;
        case "cost":
          return intervalData.cost;
        default:
          // Default to success rate
          return intervalData.total > 0
            ? Math.round((intervalData.completed / intervalData.total) * 100)
            : 0;
      }
    });

    return {
      name: workflowName,
      data: metricValues,
    };
  });

  return {
    labels: timeLabels,
    values,
  };
};

export const transformWorkflowRunsToTimelineChartData = (
  workflowRuns: WorkflowRun[],
  timeFrame: string,
  _startTime: string,
  endTime: string
): {
  labels: string[];
  values: {
    name: string;
    data: {
      x: string;
      y: number[];
    }[];
  }[];
} => {
  if (!workflowRuns || workflowRuns.length === 0) {
    return {
      labels: [],
      values: [],
    };
  }

  // Sort runs by creation time
  const sortedRuns = [...workflowRuns].sort(
    (a, b) => moment(a.created_on).valueOf() - moment(b.created_on).valueOf()
  );

  if (sortedRuns.length === 0) {
    return { labels: [], values: [] };
  }

  // Determine time intervals based on timeFrame
  let intervalMinutes = 10; // Default for "1h"

  switch (timeFrame) {
    case "1h":
      intervalMinutes = 10; // 10 minute intervals
      break;
    case "6h":
      intervalMinutes = 60; // 1 hour intervals
      break;
    case "12h":
      intervalMinutes = 120; // 2 hour intervals
      break;
    case "24h":
      intervalMinutes = 240; // 4 hour intervals
      break;
    default:
      intervalMinutes = 10; // Default to 10 minute intervals
  }

  const startTime = moment(_startTime);
  const latestRun = moment(endTime);

  // Generate time labels based on interval
  const timeLabels: number[] = [];
  let currentTime = moment(startTime);
  while (currentTime <= latestRun) {
    timeLabels.push(currentTime.valueOf());
    currentTime = moment(currentTime).add(intervalMinutes, "minutes");
  }

  // Filter runs to include only those within the time window based on matching interval
  const filteredRuns = sortedRuns;

  if (filteredRuns.length === 0) {
    return {
      labels: [],
      values: [],
    };
  }

  // Group runs by status (COMPLETED or FAILED)
  const successfulRuns: WorkflowRun[] = [];
  const failedRuns: WorkflowRun[] = [];

  filteredRuns.forEach((run) => {
    if (run.status === "COMPLETED") {
      successfulRuns.push(run);
    } else if (run.status === "FAILED") {
      failedRuns.push(run);
    }
  });

  // Group successful runs by workflow name with matching interval rounding
  const successfulByWorkflow: Record<string, number[]> = {};
  successfulRuns.forEach((run) => {
    const workflowName = run.workflow_name?.name || "Unknown Workflow";
    if (!successfulByWorkflow[workflowName]) {
      successfulByWorkflow[workflowName] = [];
    }
    const runTime = moment(run.created_on);
    if (runTime < startTime || runTime > latestRun) {
      return;
    }
    let labelTime = moment(startTime);
    let matchingLabel = 0;
    while (labelTime <= latestRun) {
      const nextLabelTime = moment(labelTime).add(intervalMinutes, "minutes");
      if (runTime >= labelTime && runTime < nextLabelTime) {
        matchingLabel = labelTime.valueOf();
        break;
      }
      labelTime.add(intervalMinutes, "minutes");
    }
    if (matchingLabel) {
      successfulByWorkflow[workflowName].push(matchingLabel, matchingLabel + 50000);
    }
  });

  // Group failed runs by workflow name with matching interval rounding
  const failedByWorkflow: Record<string, number[]> = {};
  failedRuns.forEach((run) => {
    const workflowName = run.workflow_name?.name || "Unknown Workflow";
    if (!failedByWorkflow[workflowName]) {
      failedByWorkflow[workflowName] = [];
    }
    const runTime = moment(run.created_on);
    if (runTime < startTime || runTime > latestRun) {
      return;
    }
    let labelTime = moment(startTime);
    let matchingLabel = 0;
    while (labelTime <= latestRun) {
      const nextLabelTime = moment(labelTime).add(intervalMinutes, "minutes");
      if (runTime >= labelTime && runTime < nextLabelTime) {
        matchingLabel = labelTime.valueOf();
        break;
      }
      labelTime.add(intervalMinutes, "minutes");
    }
    if (matchingLabel) {
      failedByWorkflow[workflowName].push(matchingLabel, matchingLabel + 50000);
    }
  });

  // Create the final data structure
  const result = [];

  // Add successful runs data
  if (Object.keys(successfulByWorkflow).length > 0) {
    result.push({
      name: "Successful Run",
      data:
        Object.entries(successfulByWorkflow).map(([workflowName, timestamps]) => ({
          x: workflowName,
          y: timestamps ?? [],
        })) ?? [],
    });
  }

  // Add failed runs data
  if (Object.keys(failedByWorkflow).length > 0) {
    result.push({
      name: "Failed Run",
      data:
        Object.entries(failedByWorkflow).map(([workflowName, timestamps]) => ({
          x: workflowName,
          y: timestamps ?? [],
        })) ?? [],
    });
  }

  // Use the union of workflow names as labels
  const labels = Array.from(
    new Set([...Object.keys(successfulByWorkflow), ...Object.keys(failedByWorkflow)])
  );

  return {
    labels: labels,
    values: result,
  };
};
